/**
 * Vital Signs Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Heart, Save, Gauge, Wind, ThermometerSun, Droplet } from "lucide-react";
import { toast } from "sonner";

export function VitalSignsOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    blood_pressure_sys: "",
    blood_pressure_dia: "",
    heart_rate: "",
    respiratory_rate: "",
    temperature: "",
    oxygen_saturation: "",
    weight: "",
    blood_glucose: "",
    notes: "",
  });

  const handleSave = () => {
    toast.success("Vital signs recorded");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="vital-signs-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-5 h-5 text-red-400" />
        <h3 className="font-semibold text-white">Vital Signs</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-slate-200"><Gauge className="w-4 h-4" />Blood Pressure</Label>
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="Sys" value={formData.blood_pressure_sys} onChange={(e) => setFormData({...formData, blood_pressure_sys: e.target.value})} className="w-20 bg-slate-800 border-slate-700 text-white" />
            <span className="text-slate-400">/</span>
            <Input type="number" placeholder="Dia" value={formData.blood_pressure_dia} onChange={(e) => setFormData({...formData, blood_pressure_dia: e.target.value})} className="w-20 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">mmHg</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-slate-200"><Heart className="w-4 h-4" />Heart Rate</Label>
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="HR" value={formData.heart_rate} onChange={(e) => setFormData({...formData, heart_rate: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">bpm</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-slate-200"><Wind className="w-4 h-4" />Respiratory Rate</Label>
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="RR" value={formData.respiratory_rate} onChange={(e) => setFormData({...formData, respiratory_rate: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">breaths/min</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-slate-200"><ThermometerSun className="w-4 h-4" />Temperature</Label>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.1" placeholder="Temp" value={formData.temperature} onChange={(e) => setFormData({...formData, temperature: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">°F</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1 text-slate-200"><Droplet className="w-4 h-4" />O2 Saturation</Label>
          <div className="flex items-center gap-2">
            <Input type="number" placeholder="SpO2" value={formData.oxygen_saturation} onChange={(e) => setFormData({...formData, oxygen_saturation: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">%</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Weight</Label>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.1" placeholder="Weight" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
            <span className="text-sm text-slate-400">lbs</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Blood Glucose (if applicable)</Label>
        <div className="flex items-center gap-2">
          <Input type="number" placeholder="BG" value={formData.blood_glucose} onChange={(e) => setFormData({...formData, blood_glucose: e.target.value})} className="w-24 bg-slate-800 border-slate-700 text-white" />
          <span className="text-sm text-slate-400">mg/dL</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Notes</Label>
        <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Any abnormal findings..." rows={2} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Vitals</Button>
      </div>
    </div>
  );
}

export default VitalSignsOverlay;
